import { FC, useMemo } from 'react'
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Empty,
  Image,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  useTrainingDetail,
  useRegisterTrainingRound,
  useCancelTrainingBooking,
} from 'hooks/useTraining'
import { isEmailRequiredError } from 'services/eventCalendarApi'
import { TrainingRound } from 'services/interface/eventCalendar/eventCalendar.interface'
import { formatDates, thaiDate } from 'utils/dateFormat'
import { seatLabel, timeRange } from 'utils/trainingFormat'
import NeedEmailModal, { useRequireEmail } from './NeedEmailModal'

const { Title, Paragraph, Text } = Typography

interface Props {
  eventId?: string
  open: boolean
  onClose: () => void
}

const TrainingDetailDrawer: FC<Props> = ({ eventId, open, onClose }) => {
  const { data, isLoading } = useTrainingDetail(open ? eventId : undefined)
  const registerMutation = useRegisterTrainingRound()
  const cancelMutation = useCancelTrainingBooking()
  const { ensureEmail, needEmailOpen, openNeedEmail, closeNeedEmail } = useRequireEmail()

  const event = data?.data

  // useMemo อ้าง event ตัวเดียว — ถ้า dep เป็น `event?.rounds ?? []` array ใหม่จะถูก
  // สร้างทุก render ทำให้ memo ไม่ทำงาน (eslint react-hooks เตือนไว้ถูกแล้ว)
  const rounds = useMemo<TrainingRound[]>(() => event?.rounds ?? [], [event])
  const myBookings = useMemo(() => event?.my_bookings ?? [], [event])

  /** round_id ที่ตัวเองจองไว้ → ใช้สลับปุ่มจอง/ยกเลิก */
  const bookedRoundIds = useMemo(
    () => new Map(myBookings.filter(b => b.round_id).map(b => [b.round_id as string, b.id])),
    [myBookings]
  )

  const today = formatDates(new Date())

  /** เรียงตามวัน-เวลา และตัดรอบที่ผ่านมาแล้วทิ้ง — เหลือเฉพาะรอบที่ยังเข้าร่วมได้ */
  const upcomingRounds = useMemo(
    () =>
      rounds
        .filter(round => round.round_date >= today)
        .sort((a, b) =>
          a.round_date === b.round_date
            ? a.start_time.localeCompare(b.start_time)
            : a.round_date.localeCompare(b.round_date)
        ),
    [rounds, today]
  )

  const handleRegister = async (round: TrainingRound) => {
    if (!eventId) return
    // ด่าน UX — ด่านจริงอยู่ฝั่ง server
    if (!ensureEmail()) return

    try {
      await registerMutation.mutateAsync({
        event_id: eventId,
        round_id: round.id,
      })
      message.success(`จองรอบ ${round.round_date} ${round.start_time} เรียบร้อยแล้ว`)
    } catch (error: any) {
      if (isEmailRequiredError(error)) {
        openNeedEmail()

        return
      }
      message.error(
        error?.response?.data?.description || error?.response?.data?.message || 'จองรอบไม่สำเร็จ'
      )
    }
  }

  const handleCancel = (round: TrainingRound) => {
    const attendeeId = bookedRoundIds.get(round.id)
    if (!attendeeId) return

    Modal.confirm({
      title: 'ยกเลิกการจอง',
      content: `ยืนยันยกเลิกการจองรอบ ${round.round_date} ${round.start_time}?`,
      okText: 'ยกเลิกการจอง',
      okButtonProps: { danger: true },
      cancelText: 'ปิด',
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(attendeeId)
          message.success('ยกเลิกการจองเรียบร้อยแล้ว')
        } catch {
          message.error('ยกเลิกการจองไม่สำเร็จ')
        }
      },
    })
  }

  return (
    <>
      <Drawer open={open} onClose={onClose} title="รายละเอียดการอบรม" placement="right" width={520}>
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        )}

        {!isLoading && !event && <Empty description="ไม่พบข้อมูลหลักสูตร" />}

        {event && (
          <>
            {event.cover_image_url && (
              <Image
                src={event.cover_image_url}
                alt={event.name}
                style={{ width: '100%', borderRadius: 8, marginBottom: 16 }}
                preview={false}
              />
            )}

            <Title level={4} style={{ marginBottom: 4 }}>
              {event.name}
            </Title>
            <Text type="secondary">{event.code}</Text>

            <div style={{ marginTop: 12 }}>
              <Space size={[4, 4]} wrap>
                {event.training_format?.training_type && (
                  <Tag color={event.training_format.training_type === 'Online' ? 'blue' : 'green'}>
                    {event.training_format.training_type === 'Online'
                      ? 'อบรมออนไลน์'
                      : 'อบรมที่สถานที่'}
                  </Tag>
                )}
                {event.instructor && <Tag>วิทยากร: {event.instructor}</Tag>}
              </Space>
            </div>

            <Divider>รอบอบรม</Divider>

            {upcomingRounds.length === 0 ? (
              <Empty description="หลักสูตรนี้ยังไม่เปิดรอบอบรม" />
            ) : (
              <>
                {/* สูงประมาณ 2 การ์ดครึ่ง — ใบที่ 3 โผล่ครึ่งใบให้รู้ว่าเลื่อนดูต่อได้ */}
                <div style={{ maxHeight: 230, overflowY: 'auto' }}>
                  <List
                    dataSource={upcomingRounds}
                    renderItem={round => {
                      const isBooked = bookedRoundIds.has(round.id)

                      return (
                        <List.Item
                          actions={[
                            isBooked ? (
                              <Button
                                danger
                                size="small"
                                loading={cancelMutation.isPending}
                                onClick={() => handleCancel(round)}
                              >
                                ยกเลิกการจอง
                              </Button>
                            ) : (
                              <Button
                                type="primary"
                                size="small"
                                disabled={round.is_full}
                                loading={registerMutation.isPending}
                                onClick={() => handleRegister(round)}
                              >
                                {round.is_full ? 'เต็มแล้ว' : 'จองรอบนี้'}
                              </Button>
                            ),
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Space size={4} wrap>
                                <span>{thaiDate(round.round_date)}</span>
                                {isBooked && <Tag color="blue">จองแล้ว</Tag>}
                                {round.is_full && !isBooked && <Tag color="red">เต็ม</Tag>}
                              </Space>
                            }
                            description={
                              <>
                                <Text>
                                  เวลา {timeRange(round.start_time, round.end_time)}
                                  {round.title ? ` · ${round.title}` : ''}
                                </Text>
                                <br />
                                <Text type="secondary">
                                  จองแล้ว {round.booked_count} คน · {seatLabel(round)}
                                </Text>
                              </>
                            }
                          />
                        </List.Item>
                      )
                    }}
                  />
                </div>

                {myBookings.length > 0 && (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="success"
                    showIcon
                    message={`คุณจองหลักสูตรนี้ไว้ ${myBookings.length} รอบ`}
                    description="ระบบจะส่งลิงก์เข้าร่วมและเอกสารไปที่อีเมลของคุณ"
                  />
                )}
              </>
            )}

            <Divider>รายละเอียด</Divider>

            {event.activity_details && <Paragraph>{event.activity_details}</Paragraph>}

            {event.target_audience && (
              <>
                <Text strong>เหมาะกับใคร</Text>
                <Paragraph type="secondary">{event.target_audience}</Paragraph>
              </>
            )}

            {event.takeaways && (
              <>
                <Text strong>สิ่งที่จะได้รับ</Text>
                <Paragraph type="secondary">{event.takeaways}</Paragraph>
              </>
            )}

            {event.training_format?.training_type === 'Onsite' &&
              event.training_format.venue_name && (
                <>
                  <Text strong>สถานที่</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                    {event.training_format.venue_name}
                    {event.training_format.venue_address
                      ? ` — ${event.training_format.venue_address}`
                      : ''}
                  </Paragraph>
                  {event.training_format.venue_map_url && (
                    <a href={event.training_format.venue_map_url} target="_blank" rel="noreferrer">
                      ดูแผนที่
                    </a>
                  )}
                </>
              )}

            {!!event.documents?.length && (
              <>
                <Divider />
                <Text strong>เอกสารประกอบ</Text>
                <List
                  size="small"
                  dataSource={event.documents}
                  renderItem={doc => (
                    <List.Item>
                      <a href={doc.url} target="_blank" rel="noreferrer">
                        {doc.label || doc.filename}
                      </a>
                    </List.Item>
                  )}
                />
              </>
            )}
          </>
        )}
      </Drawer>

      <NeedEmailModal open={needEmailOpen} onClose={closeNeedEmail} />
    </>
  )
}

export default TrainingDetailDrawer
